// 引入 React 的状态、生命周期和引用能力。
import { useState, useEffect, useRef } from 'react';
// 引入 Next 路由对象。
import { useRouter } from 'next/router';
// 引入登录信息接口与类型。
import { getUserLoginInfo, IUserLoginInfo } from '@/api/user';
// 引入未读消息数接口。
import { getUserMessages } from '@/api/message';
// 引入 Promise 状态枚举。
import { PromiseState } from '@/constants';
// 引入 SLS 上报能力与指标枚举。
import useSls, { MetricTypes } from '@/hooks/useSls';
// 引入 Toast 组件。
import Toast from '@/components/Toast';

// 定义轮询间隔（毫秒）。
const POLLING_INTERVAL = 20000;
// 定义 APP 场景初始化同步标记。
const APP_SCENE_SYNC_FLAG = '__USER_CENTER_LOGIN_INFO_APP_SCENE_SYNCED__';
// 定义窗口聚焦最小触发间隔（毫秒）。
const WINDOW_FOCUS_MIN_INTERVAL = 3000;
// 定义强制同步未读数标记。
const UNREAD_FORCE_SYNC_FLAG = '__USER_CENTER_UNREAD_FORCE_SYNC__';
// 定义路由回到用户中心最小触发间隔（毫秒）。
const ROUTE_RETURN_MIN_INTERVAL = 100;
// 定义登录态失效提示文案。
const AUTH_EXPIRED_MESSAGE = '登录态已失效，正在跳转';
// 定义登录态失效后的延迟跳转时间（毫秒）。
const AUTH_REDIRECT_DELAY = 600;

// 定义登录信息同步场景类型。
type LoginInfoScene = 'app' | 'windowFocus' | 'polling' | 'routeReturn';

// 解析 URL 的 pathname。
const resolvePathname = (url: string) => {
  // URL 为空时返回空字符串。
  if (!url) return '';
  // 尝试用标准 URL 解析。
  try {
    // SSR 环境直接返回空字符串。
    if (typeof window === 'undefined') return '';
    // 返回解析出的 pathname。
    return new URL(url, window.location.origin).pathname;
  } catch (e) {
    // 解析失败时降级按 ? 分割。
    return String(url).split('?')[0];
  }
};

// 判断是否用户中心路径。
const isUserCenterPath = (pathname: string) =>
  // 命中用户中心路径则返回 true。
  pathname === '/user-center' || pathname === '/pro/user-center';

// 判断是否消息页路径。
const isMessagePath = (pathname: string) =>
  // 命中消息页路径则返回 true。
  pathname === '/message' || pathname === '/pro/message' || pathname === '/messages' || pathname === '/pro/messages';

// 判断是否积分页路径。
const isPointsPath = (pathname: string) =>
  // 命中积分页路径则返回 true。
  pathname === '/points' || pathname === '/pro/points';

// 判断是否未授权错误。
const isUnauthorizedError = (e: any) =>
  // 状态码为 401 或错误文案为 unauthorized 视为未授权。
  Number(e?.status ?? e?.response?.status ?? e?.code ?? e?.error?.code) === 401
  || String(e?.message ?? e?.error?.message ?? '').toLowerCase() === 'unauthorized';

// 判断账户是否已登录。
const isLoginAccount = (account: any) => !!account?.id;

// 判断当前 URL 是否是“本人用户中心视图”。
const isSelfUserCenterView = (url: string, account: any) => {
  // 获取登录用户 ID。
  const loginId = Number(account?.id);
  // 登录 ID 无效时返回 false。
  if (!Number.isFinite(loginId)) {
    // 返回非本人视图。
    return false;
  }
  // 尝试解析 URL 查询参数。
  try {
    // 构建 URL 对象。
    const parsedUrl = new URL(url, window.location.origin);
    // 读取 query 中的 uid。
    const uidFromQuery = Number(parsedUrl.searchParams.get('uid'));
    // 未传 uid 时默认按本人视图处理。
    if (!Number.isFinite(uidFromQuery)) {
      // 返回本人视图。
      return true;
    }
    // uid 相同则是本人视图。
    return uidFromQuery === loginId;
  } catch (e) {
    // URL 解析失败时保守按本人视图处理。
    return true;
  }
};

// 定义 useLoginInfo Hook。
const useLoginInfo = () => {
  // 获取路由对象。
  const router = useRouter();
  // 维护 account 状态。
  const [account, setAccount] = useState({ status: PromiseState.PENDING });
  // 保存轮询定时器引用。
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // 标记组件是否已挂载。
  const isMountedRef = useRef(false);
  // 获取 SLS 上报函数。
  const { reportSlsData } = useSls();
  // 记录最近一次窗口聚焦同步时间戳。
  const lastWindowFocusTsRef = useRef(0);
  // 记录最近一次路由返回同步时间戳。
  const lastRouteReturnTsRef = useRef(0);
  // 记录最近一次路由 pathname。
  const routePathRef = useRef('');
  // 防止未授权处理重复触发。
  const hasHandledMessagesUnauthorizedRef = useRef(false);
  // 保存 account 最新值，供异步回调读取。
  const accountRef = useRef<any>({ status: PromiseState.PENDING });

  // 处理 /users/messages 未授权场景。
  const handleMessagesUnauthorized = () => {
    // 已处理过则直接返回。
    if (hasHandledMessagesUnauthorizedRef.current) {
      // 结束当前流程。
      return;
    }
    // 标记已处理。
    hasHandledMessagesUnauthorizedRef.current = true;
    // 提示用户登录态失效。
    Toast.show({ content: AUTH_EXPIRED_MESSAGE });
    // 记录当前 pathname。
    const currentPath = window.location.pathname;
    // 延迟执行重定向逻辑，避免突兀切页。
    window.setTimeout(() => {
      // 用户中心页直接刷新触发统一流程。
      if (isUserCenterPath(currentPath)) {
        // 刷新当前页面。
        window.location.reload();
        // 结束当前流程。
        return;
      }
      // 积分页或消息页跳转首页。
      if (isPointsPath(currentPath) || isMessagePath(currentPath)) {
        // 跳转到首页。
        window.location.href = '/';
        // 结束当前流程。
        return;
      }
      // 其他页面允许后续再次触发该处理逻辑。
      hasHandledMessagesUnauthorizedRef.current = false;
    }, AUTH_REDIRECT_DELAY);
  };

  // 按场景拉取登录态或未读消息。
  const fetchUserInfo = (scene: LoginInfoScene) => {
    // 记录请求开始时间。
    const requestTimestamp = Date.now();
    // 记录当前 pathname。
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    // 记录当前 query string。
    const search = typeof window !== 'undefined' ? window.location.search : '';
    // 判断是否需要上报用户中心埋点。
    const shouldReportUserCenter = pathname === '/user-center' || pathname === '/pro/user-center';
    // 解析查询参数。
    const params = new URLSearchParams(search);
    // 获取 uid 参数。
    const uidFromQuery = Number(params.get('uid'));
    // 判断终端类型。
    const terminalType = /mobile|android|iphone|ipad|ipod|harmony/i.test(window.navigator.userAgent) ? 'm' : 'pc';

    // polling/windowFocus 场景只拉取未读消息数。
    if (scene === 'polling' || scene === 'windowFocus') {
      // 未登录时不调用未读接口。
      if (!isLoginAccount(accountRef.current)) {
        // 结束当前流程。
        return;
      }
      // 调用未读消息接口。
      getUserMessages().then((res) => {
        // 组件仍挂载时才更新状态。
        if (isMountedRef.current) {
          // 按函数式写法更新 account。
          setAccount((prev: any) => {
            // 仅在 pending 时将状态推进到 fulfilled。
            const nextStatus = prev?.status === PromiseState.PENDING ? PromiseState.FULFILLED : prev?.status;
            // 判断未读数是否变化。
            const isMessagesChanged = prev?.messages !== res.messages;
            // 判断是否需要强制做一次未读同步。
            const shouldForceUnreadSync = Boolean((window as any)[UNREAD_FORCE_SYNC_FLAG]) && Number(res.messages) > 0;
            // 未读未变化且状态未变化且无强制同步时直接复用旧状态。
            if (!isMessagesChanged && nextStatus === prev?.status && !shouldForceUnreadSync) {
              // 返回旧状态避免无效重渲染。
              return prev;
            }
            // 命中强制同步后清理标记。
            if (shouldForceUnreadSync) {
              // 重置强制同步标记。
              (window as any)[UNREAD_FORCE_SYNC_FLAG] = false;
            }
            // 返回合并后的新状态。
            return {
              // 保留旧字段。
              ...prev,
              // 更新未读消息数。
              messages: res.messages,
              // 更新状态字段。
              status: nextStatus,
            };
          });
        }

        // 需要上报时写入成功埋点。
        if (shouldReportUserCenter) {
          // 计算接口耗时。
          const duration = Date.now() - requestTimestamp;
          // 上报 SLS 指标与环境参数。
          reportSlsData([
            // 上报耗时指标。
            ['reportMetric', MetricTypes.userCenterLoginInfoApiTime, duration],
            // 写入接口环境维度。
            [
              'setEnvironment',
              {
                // 接口路径。
                api_path: '/users/messages',
                // 接口参数。
                api_params: '{}',
                // 访问用户 uid。
                uid: Number.isFinite(uidFromQuery) ? uidFromQuery : null,
                // 成功标记。
                is_success: 1,
                // 失败原因为空。
                fail_reason: '',
                // 响应码。
                response_code: 200,
                // 终端类型。
                terminal_type: terminalType,
                // 阶段标记。
                stage: 'post_ssr',
                // 场景值。
                scene: scene,
                // 视图类型。
                user_center_view_type: Number.isFinite(uidFromQuery) ? 'visitor' : 'self',
                // 页面路由。
                page_route: pathname,
                // 路由参数。
                route_params: search || '',
              },
            ],
          ]);
        }
      }).catch((e: any) => {
        // 未授权时触发统一兜底逻辑。
        if (isUnauthorizedError(e)) {
          // 处理未授权跳转。
          handleMessagesUnauthorized();
        }
        // 需要上报时写入失败埋点。
        if (shouldReportUserCenter) {
          // 计算接口耗时。
          const duration = Date.now() - requestTimestamp;
          // 提取响应码。
          const responseCode = e?.code ?? e?.status ?? e?.response?.status ?? -1;
          // 提取失败原因。
          const failReason = e?.message || e?.code || 'request_failed';
          // 上报 SLS 指标与环境参数。
          reportSlsData([
            // 上报耗时指标。
            ['reportMetric', MetricTypes.userCenterLoginInfoApiTime, duration],
            // 写入接口环境维度。
            [
              'setEnvironment',
              {
                // 接口路径。
                api_path: '/users/messages',
                // 接口参数。
                api_params: '{}',
                // 访问用户 uid。
                uid: Number.isFinite(uidFromQuery) ? uidFromQuery : null,
                // 失败标记。
                is_success: 0,
                // 失败原因。
                fail_reason: failReason,
                // 响应码。
                response_code: responseCode,
                // 终端类型。
                terminal_type: terminalType,
                // 阶段标记。
                stage: 'post_ssr',
                // 场景值。
                scene: scene,
                // 视图类型。
                user_center_view_type: Number.isFinite(uidFromQuery) ? 'visitor' : 'self',
                // 页面路由。
                page_route: pathname,
                // 路由参数。
                route_params: search || '',
              },
            ],
          ]);
        }
      });
      // polling/windowFocus 分支执行到此结束。
      return;
    }

    // 其他场景拉取完整登录信息。
    getUserLoginInfo(false, {}, requestTimestamp).then((res: IUserLoginInfo) => {
      // 组件仍挂载时才更新状态。
      if (isMountedRef.current) {
        // 写入完整 account 与 fulfilled 状态。
        setAccount({ ...res, status: PromiseState.FULFILLED });
      }

      // 需要上报时写入成功埋点。
      if (shouldReportUserCenter) {
        // 计算接口耗时。
        const duration = Date.now() - requestTimestamp;
        // 获取登录用户 id。
        const loginUid = Number(res?.id);
        // 计算上报 uid。
        const reportUid = Number.isFinite(uidFromQuery) ? uidFromQuery : (Number.isFinite(loginUid) ? loginUid : null);
        // 计算视图类型（本人/访客）。
        const viewType = Number.isFinite(uidFromQuery) && Number.isFinite(loginUid) && uidFromQuery !== loginUid ? 'visitor' : 'self';

        // 上报 SLS 指标与环境参数。
        reportSlsData([
          // 上报耗时指标。
          ['reportMetric', MetricTypes.userCenterLoginInfoApiTime, duration],
          // 写入接口环境维度。
          [
            'setEnvironment',
            {
              // 接口路径。
              api_path: '/users/login-info',
              // 接口参数。
              api_params: JSON.stringify({ t: requestTimestamp }),
              // 访问用户 uid。
              uid: reportUid,
              // 成功标记。
              is_success: 1,
              // 失败原因为空。
              fail_reason: '',
              // 响应码。
              response_code: 200,
              // 终端类型。
              terminal_type: terminalType,
              // 阶段标记。
              stage: 'post_ssr',
              // 场景值。
              scene: scene,
              // 视图类型。
              user_center_view_type: viewType,
              // 页面路由。
              page_route: pathname,
              // 路由参数。
              route_params: search || '',
            },
          ],
        ]);
      }
    }).catch((e: any) => {
      // 组件仍挂载时才更新失败状态。
      if (isMountedRef.current) {
        // 仅写入 rejected 状态。
        setAccount({ status: PromiseState.REJECTED });
      }

      // 需要上报时写入失败埋点。
      if (shouldReportUserCenter) {
        // 计算接口耗时。
        const duration = Date.now() - requestTimestamp;
        // 提取响应码。
        const responseCode = e?.code ?? e?.status ?? e?.response?.status ?? -1;
        // 提取失败原因。
        const failReason = e?.message || e?.code || 'request_failed';
        // 上报 SLS 指标与环境参数。
        reportSlsData([
          // 上报耗时指标。
          ['reportMetric', MetricTypes.userCenterLoginInfoApiTime, duration],
          // 写入接口环境维度。
          [
            'setEnvironment',
            {
              // 接口路径。
              api_path: '/users/login-info',
              // 接口参数。
              api_params: JSON.stringify({ t: requestTimestamp }),
              // 访问用户 uid。
              uid: Number.isFinite(uidFromQuery) ? uidFromQuery : null,
              // 失败标记。
              is_success: 0,
              // 失败原因。
              fail_reason: failReason,
              // 响应码。
              response_code: responseCode,
              // 终端类型。
              terminal_type: terminalType,
              // 阶段标记。
              stage: 'post_ssr',
              // 场景值。
              scene: scene,
              // 视图类型。
              user_center_view_type: Number.isFinite(uidFromQuery) ? 'visitor' : 'self',
              // 页面路由。
              page_route: pathname,
              // 路由参数。
              route_params: search || '',
            },
          ],
        ]);
      }
    });
  };

  // 对外暴露统一同步入口。
  const syncLoginState = (scene: LoginInfoScene) => {
    // 按场景执行拉取逻辑。
    fetchUserInfo(scene);
  };

  // 同步 account 最新值到 ref。
  useEffect(() => {
    // 写入最新 account。
    accountRef.current = account;
  }, [account]);

  // 初始化事件、轮询与路由监听。
  useEffect(() => {
    // 标记组件已挂载。
    isMountedRef.current = true;
    // 初始化当前路由路径缓存。
    routePathRef.current = resolvePathname(router.asPath || window.location.href);
    // app 场景仅全局触发一次。
    if (!(window as any)[APP_SCENE_SYNC_FLAG]) {
      // 写入全局已同步标记。
      (window as any)[APP_SCENE_SYNC_FLAG] = true;
      // 触发 app 场景同步。
      syncLoginState('app');
    }

    // 窗口回前台同步的节流包装函数。
    const trySyncWindowFocus = () => {
      // 读取当前时间戳。
      const now = Date.now();
      // 在节流窗口内则跳过同步。
      if (now - lastWindowFocusTsRef.current < WINDOW_FOCUS_MIN_INTERVAL) {
        // 结束当前流程。
        return;
      }
      // 更新最近触发时间戳。
      lastWindowFocusTsRef.current = now;
      // 触发 windowFocus 场景同步。
      syncLoginState('windowFocus');
    };

    // 停止轮询函数。
    const stopPolling = () => {
      // 无定时器时直接返回。
      if (!timerRef.current) return;
      // 清理定时器。
      clearInterval(timerRef.current);
      // 重置引用。
      timerRef.current = null;
    };

    // 启动轮询函数。
    const startPolling = () => {
      // 已有定时器时不重复启动。
      if (timerRef.current) return;
      // 页面不可见时不启动。
      if (document.visibilityState !== 'visible') return;
      // 无焦点时不启动。
      if (typeof document.hasFocus === 'function' && !document.hasFocus()) return;
      // 创建轮询定时器。
      timerRef.current = setInterval(() => {
        // 页面不可见时跳过本次执行。
        if (document.visibilityState !== 'visible') return;
        // 无焦点时跳过本次执行。
        if (typeof document.hasFocus === 'function' && !document.hasFocus()) return;
        // 触发 polling 场景同步。
        syncLoginState('polling');
      }, POLLING_INTERVAL);
    };

    // 页面可见性变化处理函数。
    const onVisibilityChange = () => {
      // 可见时重启轮询并做一次前台同步。
      if (document.visibilityState === 'visible') {
        // 启动轮询。
        startPolling();
        // 执行节流后的前台同步。
        trySyncWindowFocus();
      } else {
        // 不可见时停止轮询。
        stopPolling();
      }
    };

    // 窗口获得焦点处理函数。
    const onWindowFocus = () => {
      // 获得焦点时启动轮询。
      startPolling();
      // 执行节流后的前台同步。
      trySyncWindowFocus();
    };

    // 窗口失焦处理函数。
    const onWindowBlur = () => {
      // 失焦时停止轮询。
      stopPolling();
    };

    // 初始化时按当前前后台状态尝试启动轮询。
    startPolling();

    // 监听页面可见性变化。
    document.addEventListener('visibilitychange', onVisibilityChange);
    // 监听窗口 focus 事件。
    window.addEventListener('focus', onWindowFocus);
    // 监听窗口 blur 事件。
    window.addEventListener('blur', onWindowBlur);

    // 路由切换完成处理函数。
    const onRouteChangeComplete = (nextUrl: string) => {
      // 读取上一个路径。
      const previousPath = routePathRef.current;
      // 解析当前路径。
      const currentPath = resolvePathname(nextUrl);
      // 更新路径缓存。
      routePathRef.current = currentPath;
      // 仅在进入用户中心且不是用户中心内跳转时处理。
      if (!isUserCenterPath(currentPath) || isUserCenterPath(previousPath)) {
        // 条件不满足直接返回。
        return;
      }
      // 非本人用户中心视图不触发 routeReturn 同步。
      if (!isSelfUserCenterView(nextUrl, accountRef.current)) {
        // 结束当前流程。
        return;
      }
      // 读取当前时间戳。
      const now = Date.now();
      // 在最小间隔内则跳过。
      if (now - lastRouteReturnTsRef.current < ROUTE_RETURN_MIN_INTERVAL) {
        // 结束当前流程。
        return;
      }
      // 更新最近 routeReturn 触发时间戳。
      lastRouteReturnTsRef.current = now;
      // 触发 routeReturn 场景同步。
      syncLoginState('routeReturn');
    };

    // 订阅路由切换完成事件。
    router.events.on('routeChangeComplete', onRouteChangeComplete);

    // 返回 effect 清理函数。
    return () => {
      // 标记组件已卸载。
      isMountedRef.current = false;
      // 停止轮询并清理计时器。
      stopPolling();
      // 取消页面可见性监听。
      document.removeEventListener('visibilitychange', onVisibilityChange);
      // 取消窗口 focus 监听。
      window.removeEventListener('focus', onWindowFocus);
      // 取消窗口 blur 监听。
      window.removeEventListener('blur', onWindowBlur);
      // 取消路由切换完成监听。
      router.events.off('routeChangeComplete', onRouteChangeComplete);
    };
  }, [router.events]); // eslint-disable-line react-hooks/exhaustive-deps

  // 返回 account 状态供上层消费。
  return account;
};

// 导出 Hook。
export default useLoginInfo;
